import React from 'react';
import {connect} from 'react-redux';
import _ from 'lodash';
import moment from 'moment';
import GLOBAL_FILTER_CONTROLLER from '../../../controllers/globalFilter';
import {FIELDS} from '../../filter/GlobalFilterPanel';
import LanguageStringResolver from '../../fields/language/LanguageStringResolver';
import SingleCompanySelector from '../../selectors/company/SingleCompanySelector';
import SingleCountrySelector from '../../selectors/country/SingleCountrySelector';
import SimpleDateSelector from '../../selectors/date/SimpleDateSelector';
import SingleCurrencySelector from '../../selectors/currency/SingleCurrencySelector';
import {VATBookLineType, FilingTypeEnum} from '../../../utils/enums';
import ApplicationMessages from '../../inAppMessages/ApplicationMessages';
import HighlightOnInAppMessage from '../../inAppMessages/HighlightOninAppMessage';
import ManualDocumentsVATReturnsLineEditor from './ManualDocumentsVATReturnsLineEditor';
import Logger, {LOG_LEVELS, ALL_MESSAGES} from '../../../utils/logger';
import ReactComponentHelper from "../../helper/ReactComponentHelper";

const FORM_NAME = 'MANUAL_DOCUMENTS_FORM';
const log = new Logger(FORM_NAME, LOG_LEVELS.DEBUG);
const DATE_FORMAT = 'YYYY-MM-DD';

class ManualDocumentsForm extends React.Component {

    constructor(props) {
        super(props);
        const globalFilterOptions = GLOBAL_FILTER_CONTROLLER.getAll();
        let monthStartDate = null;
        if(globalFilterOptions[FIELDS.PARAM_MONTH] && globalFilterOptions[FIELDS.PARAM_YEAR]) {
            monthStartDate = moment.utc([globalFilterOptions[FIELDS.PARAM_YEAR], Number.parseInt(globalFilterOptions[FIELDS.PARAM_MONTH]) - 1]).format(DATE_FORMAT);
        } else if(globalFilterOptions[FIELDS.PARAM_START_DATE]) {
            let dateToConsiderToGetStartDate = moment.utc(globalFilterOptions[FIELDS.PARAM_START_DATE], DATE_FORMAT);
            monthStartDate = moment.utc([dateToConsiderToGetStartDate.year(), dateToConsiderToGetStartDate.month()]).format(DATE_FORMAT);
        } 
        else {
            let dateToConsiderToGetStartDate = moment.utc();
            monthStartDate = moment.utc([dateToConsiderToGetStartDate.year(), dateToConsiderToGetStartDate.month()]).format(DATE_FORMAT);
        }
        this.state = {
            selection: {
                documentType: VATBookLineType.SALES.name,
                invoiceDate: props.dateClearance ? '' : monthStartDate,
                transactionDate: props.dateClearance ? '' : monthStartDate
            },
            referenceTextRemaining: props.referenceReporterLimit - props.initData.reporterName.length
        };
        this.onSubmitHandler = this.onSubmitHandler.bind(this);
        this.selectionHandler = this.selectionHandler.bind(this);
        this.onFieldChangeHandlerFactory = this.onFieldChangeHandlerFactory.bind(this);
        this.setInitData = this.setInitData.bind(this);
        this.onLineEntryUpdate = this.onLineEntryUpdate.bind(this);
        this.onTaxTotalValuesChange = this.onTaxTotalValuesChange.bind(this);
        this.vatBoxes = [];
        this.taxTotalValues = {};
    }

    setInitData() {
        if(!_.isEmpty(this.props.initData)) {
            this.setState({
                selection: Object.assign(this.state.selection, this.props.initData)
            });
        } 
    }

    onFieldChangeHandlerFactory(fieldName) {
        log.debug('onFieldChangeHandlerFactory(): fieldName: ', fieldName);
        return (event) => {
            let selection = Object.assign({}, this.state.selection);
            selection[fieldName] = event.target.value;
            this.setState({selection: selection});
            if(typeof this.props.onSelection === 'function') {
                this.setState({
                    referenceTextRemaining: this.props.referenceReporterLimit - (selection.reporterName.length + selection.referenceCode.length)
                })
                this.props.onSelection(selection);
            }
        };
    }

    selectionHandler(keyValuePairs) {
        log.debug('selectionHandler() Key value pairs: ', keyValuePairs);    
        let key =  Object.keys(keyValuePairs)[0];
        let value =  Object.values(keyValuePairs)[0];
        let selection = Object.assign({}, this.state.selection);
        if(keyValuePairs.country !== undefined && this.props.initData?.updateCurrencyAsCountry) {
            if(ReactComponentHelper.getCurrencyByCountryCode(keyValuePairs.country)?.currencyCode) {
                selection.currency = ReactComponentHelper.getCurrencyByCountryCode(keyValuePairs.country)?.currencyCode
            } else {
                selection.currency = '';
            }
        }
        if(_.isEmpty(value)) {
          delete selection[key];
        } else {
          selection = Object.assign(selection, keyValuePairs);
        }
        this.setState({
          selection: selection
        });
        if(typeof this.props.onSelection === 'function') {
            this.props.onSelection(selection);
        }
    }

    onLineEntryUpdate(lineEntries) {
        log.debug('onLineEntryUpdate(): lineEntries: ', lineEntries);
        this.vatBoxes = lineEntries;
    }

    onTaxTotalValuesChange(taxTotalValues) {
        log.debug('onTaxTotalValuesChange(): taxTotalValues: ', taxTotalValues);
        this.taxTotalValues = taxTotalValues;
    }

    onSubmitHandler(event) {
        log.debug('onSubmitHandler(): ');
        log.clearDictionaryError(ALL_MESSAGES.BLANK_CURRENCY);
        log.clearDictionaryError(ALL_MESSAGES.BLANK_TRANSACTION_DATE);
        log.clearDictionaryError(ALL_MESSAGES.BLANK_INVOICE_DATE);
        event.preventDefault();
        const selection = Object.assign({}, this.state.selection, {vatBoxes: this.vatBoxes}, this.taxTotalValues);
        log.debug('onSubmitHandler(): ', selection);
        let fieldsOk = true;
        const currency = selection.currency && selection.currency.trim();
        const transactionDate = selection.transactionDate && selection.transactionDate.trim();
        const invoiceDate = selection.invoiceDate && selection.invoiceDate.trim();
        
        if (!currency || currency === '') {
            log.setDictionaryMessage(ALL_MESSAGES.BLANK_CURRENCY);
            fieldsOk = false;
        }
        if (!transactionDate || transactionDate === '') {
            log.setDictionaryMessage(ALL_MESSAGES.BLANK_TRANSACTION_DATE);
            fieldsOk = false;
        }
        if (!invoiceDate || invoiceDate === '') {
            log.setDictionaryMessage(ALL_MESSAGES.BLANK_INVOICE_DATE);
            fieldsOk = false;
        }
        if(!this.vatBoxes || _.isEmpty(this.vatBoxes)) {
            log.setDictionaryMessage(ALL_MESSAGES.VAT_BOX_REQUIRED);
            fieldsOk = false;
        }
        if(fieldsOk) {
            this.props.onSubmit(selection);
        }
        return fieldsOk;
    }

    render() {
        log.debug('render(): ', this.props);
        const selection = this.state.selection || {};
        const { children } = this.props;

        const childrenWithProps = React.Children.map(children, child =>
            React.cloneElement(child, { onAddClick: this.onLineEntryUpdate, onRemoveClick: this.onLineEntryUpdate, onInputChange: this.onTaxTotalValuesChange})
        );

        return (
            <div>                
                {!this.props.hideTypeOfReturnTitle &&
                    <h2 className="mt10"><LanguageStringResolver string='manualDocumentsTypeOfReturn_typeOfReturn'/></h2>
                }                
                <form className="form" onSubmit={this.onSubmitHandler}>
                    
                    <ApplicationMessages 
                            scopes={[FORM_NAME]} 
                            messageIds={[ALL_MESSAGES.BLANK_CURRENCY, ALL_MESSAGES.BLANK_TRANSACTION_DATE, ALL_MESSAGES.BLANK_INVOICE_DATE, ALL_MESSAGES.VAT_BOX_REQUIRED]} 
                            messageType="applicationError"
                    />
                    {!this.props.hideTransactionType &&
                        <s-row data-testid='type-of-return'>
                            <s-col span="6">
                                <fieldset>
                                    <legend><LanguageStringResolver string='manualDocumentsForm_SelectTransactionType' /></legend>
                                    <input
                                        className="radio"
                                        type="radio"
                                        onChange={this.onFieldChangeHandlerFactory('documentType')}
                                        name="documentType"
                                        value="SALES"
                                        checked={selection.documentType === VATBookLineType.SALES.name}
                                    />
                                    <label className="radio"><LanguageStringResolver string='manualDocumentsForm_SalesOut' /></label>
                                    <input
                                        className="radio"
                                        type="radio"
                                        onChange={this.onFieldChangeHandlerFactory('documentType')}
                                        name="documentType"
                                        value="PURCHASE"
                                        checked={selection.documentType === VATBookLineType.PURCHASE.name}
                                    />
                                    <label className="radio"><LanguageStringResolver string='manualDocumentsForm_PurchaseIn' /></label>
                                </fieldset>
                            </s-col>
                            {!this.props.hideManualDocumentType &&
                                <s-col span="6">
                                    <fieldset>
                                        <legend><LanguageStringResolver string='manualDocumentsForm_ManualDocumentType' /></legend>
                                        <input
                                            className="checkbox"
                                            type="checkbox"
                                            name='manualDocumentType'
                                            checked={true}
                                            value={FilingTypeEnum.props[FilingTypeEnum.VATRETURN].value}
                                        />
                                        <label className="checkbox"><LanguageStringResolver string='manualDocumentsForm_VATReturn' /></label>
                                    </fieldset>
                                </s-col>
                            }
                        </s-row>
                    }

                    <h2 className="mt10"><LanguageStringResolver string='manualDocumentsForm_ReportingData'/></h2>
                    {this.props.showRequiredFields &&
                        <div>
                            <label className='mb15'>
                                <LanguageStringResolver string='manualDocumentsForm_ReportingData_RequiredFields' />
                            </label>
                        </div>
                    }
                    <fieldset>
                        <s-row>
                            <s-col span="6" data-testid='company-country-selector'>
                                <label>
                                    {this.props.showRequiredFields && <strong style={{ color: 'red' }}>* </strong>}
                                    <LanguageStringResolver string='manualDocumentsForm_Company' />: </label>
                                <SingleCompanySelector
                                    className='disabled'
                                    fieldName={'companyCode'}
                                    extraProps={{
                                        disabled: !this.props.enableCompany,
                                        readOnly: 'readOnly'
                                    }}
                                    selectionHandler={this.selectionHandler}
                                    selectedCompanyId={selection.companyCode}
                                    valueCompanyCode={true}
                                />
                                <label className="mt10">
                                    {this.props.showRequiredFields && <strong style={{ color: 'red' }}>* </strong>}
                                    <LanguageStringResolver string='manualDocumentsForm_Country' />: </label>
                                <SingleCountrySelector
                                    countryList={this.props.countryList}
                                    className='disabled'
                                    fieldName={'country'}
                                    extraProps={{
                                        disabled: !this.props.enableCountry,
                                        readOnly: 'readOnly'
                                    }}
                                    selectionHandler={this.selectionHandler}
                                    selectedCountryCode={selection.country}
                                />
                                <HighlightOnInAppMessage
                                    scopes={[FORM_NAME]}
                                    messageIds={[ALL_MESSAGES.BLANK_CURRENCY]}
                                >
                                    <div className="form__field-wrapper input-container">   
                                        <label className="mt10">
                                            {this.props.showRequiredFields && <strong style={{ color: 'red' }}>* </strong>}
                                            <LanguageStringResolver string='manualDocumentsForm_Currency' />:</label>
                                        <SingleCurrencySelector
                                            fieldName={'currency'}
                                            onChange={this.selectionHandler}
                                            value={selection.currency}
                                        />
                                    </div>
                                </HighlightOnInAppMessage>        
                            </s-col>
                            <s-col span="6" data-testid='transaction-invoice-selector'>
                                <HighlightOnInAppMessage
                                    scopes={[FORM_NAME]}
                                    messageIds={[ALL_MESSAGES.BLANK_TRANSACTION_DATE]}
                                >
                                    <label>
                                        {this.props.showRequiredFields && <strong style={{ color: 'red' }}>* </strong>}
                                        <LanguageStringResolver string='manualDocumentsForm_TransactionDate' />:</label>
                                    <SimpleDateSelector
                                        selectionHandler={this.selectionHandler}
                                        fieldName='transactionDate'
                                        format="yyyy-mm-dd"
                                        value={selection.transactionDate || ''}
                                    />
                                </HighlightOnInAppMessage>

                                <HighlightOnInAppMessage
                                    scopes={[FORM_NAME]}
                                    messageIds={[ALL_MESSAGES.BLANK_INVOICE_DATE]}
                                >
                                    <label className='mt10'>
                                        {this.props.showRequiredFields && <strong style={{ color: 'red' }}>* </strong>}
                                        <LanguageStringResolver string='manualDocumentsForm_InvoiceDate' />:</label>
                                    <SimpleDateSelector
                                        selectionHandler={this.selectionHandler}
                                        fieldName='invoiceDate'
                                        format="yyyy-mm-dd"
                                        value={selection.invoiceDate || ''}
                                    />
                                </HighlightOnInAppMessage>

                                <label className="mt10"><LanguageStringResolver string='manualDocumentsForm_Reference'/>: 
                                    {this.props.referenceReporterLimit && (<> {this.state.referenceTextRemaining} <LanguageStringResolver string='manualDocumentsForm_Reference_TextLimit'/></>)}
                                </label>
                                <input
                                    maxLength={this.props.referenceReporterLimit ? (this.props.referenceReporterLimit - this.props.initData.reporterName.length) : undefined}
                                    data-testid='reference-code'
                                    type='text'
                                    fieldName={'referenceCode'}
                                    className='text-control'
                                    onChange={this.onFieldChangeHandlerFactory('referenceCode')}
                                    value={selection.referenceCode || ''}
                                />
                                <label className="mt10"><LanguageStringResolver string='manualDocumentsForm_ReporterName'/>:</label>
                                <input
                                    type='text'
                                    fieldName={'reporterName'}
                                    onChange={this.onFieldChangeHandlerFactory('reporterName')}
                                    value={selection.reporterName || ''}
                                    className='disabled'
                                    disabled={true}
                                />
                            </s-col>
                        </s-row>
                        <div>
                            {childrenWithProps}                     
                        </div>
                        {!this.props.hideSubmissionButton &&
                            <div className="mt15" data-testid='action-buttons'>
                                <button className="primary" type='submit' data-testid='submit-button'>
                                    <LanguageStringResolver string='manualDocumentsForm_Submit'/>
                                </button>
                                <button className="secondary" onClick={this.props.onCancel}>
                                    <LanguageStringResolver string='manualDocumentsForm_Cancel'/>
                                </button>
                            </div>
                        }
                    </fieldset>
                </form>
            </div>
        );
    }

    componentDidMount() {
        log.clearAllDictionaryErrors();
        log.clearDictionaryError(ALL_MESSAGES.BLANK_CURRENCY, ALL_MESSAGES.BLANK_TRANSACTION_DATE, ALL_MESSAGES.BLANK_INVOICE_DATE);
        this.setInitData();
    }
}


ManualDocumentsForm.defaultProps = {
    initData: {},
    onSubmit: () => {},
    onCancel: () => {}
};

function mapStateToProps(state, props) {
    log.debug('mapStateToProps(): ', state);
    log.debug('mapStateToProps(): props', props);

    return {
        languages: state.languages,
        countryList : (state.content && state.content.contentData && state.content.contentData.countryList)
    };
}


export default connect(mapStateToProps)(ManualDocumentsForm);

